/*
 * 消息朗读按钮。
 *
 * ## 与旧项目的差别
 *
 * 旧版是一个全局 zustand store（`packages/components/src/audio/voice.ts`）里的
 * `autoPlay` 开关 + `MessagePlayer` 对**每条新消息**自动调 TTS。三个问题：
 *   1. 自动播放意味着每条回复都要合成，而合成是要钱的（1 条 ≈ 2 灵力）。
 *   2. iOS 上没有用户手势的 `play()` 会被拒，旧代码用 `.catch(() => console.log('播放失败'))`
 *      静默吞掉，用户只会觉得"点了没反应"。
 *   3. 那个 store 实际是坏的——`const aiVoiceApi: any = {}`，调用必抛异常。
 *
 * 新版三条规则：
 *   - **默认静音**，不自动播放。每条消息一枚喇叭，点了才响。
 *   - **同一时刻只有一条在响**。播放状态提到 ChatPage 层，不是每个按钮各管各的，
 *     否则点第二条时第一条还在响，两个声音叠在一起。
 *   - **失败要说出来**，不吞异常。
 *
 * ## 为什么按钮长在消息上而不是页面顶部
 *
 * 计费单位是「条消息」不是「次播放」：一条 assistant 消息一生只合成一次、
 * 扣一次灵力，之后重播免费（见 docs/ECONOMY.md §6）。按钮跟着消息走，
 * 用户才能理解"我为这条花了钱"。放一个全局播放开关会让扣费时机变得不可预测。
 */

import { IconSpeaker, IconSpeakerMuted, IconSpeakerPlaying } from "./Icons";

/** 一条消息的语音状态。 */
export type SpeakState =
  /** 还没有音频。点击会触发合成，**要扣灵力**。 */
  | "idle"
  /** 正在合成或正在取音频。 */
  | "loading"
  /** 已有音频，静止。重播免费。 */
  | "ready"
  /** 正在响。 */
  | "playing"
  /** 上一次失败了。 */
  | "failed";

const LABEL: Record<SpeakState, string> = {
  idle: "让她读出来",
  loading: "正在准备语音",
  ready: "再听一次",
  playing: "停止播放",
  failed: "重试朗读",
};

export function SpeakButton({
  state,
  onToggle,
  disabled,
}: {
  state: SpeakState;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const label = LABEL[state];

  return (
    <button
      type="button"
      className={`speak speak--${state}`}
      onClick={(event) => {
        // 气泡本身是可点的（展开时间戳），别让点喇叭连带触发它。
        event.stopPropagation();
        onToggle();
      }}
      disabled={disabled || state === "loading"}
      aria-label={label}
      title={label}
      aria-pressed={state === "playing"}
    >
      {state === "playing" ? (
        <IconSpeakerPlaying size={15} />
      ) : state === "idle" || state === "failed" ? (
        <IconSpeakerMuted size={15} />
      ) : (
        <IconSpeaker size={15} />
      )}
      {state === "loading" ? <i className="speak__spin" aria-hidden /> : null}
    </button>
  );
}
